/**
 * InMotion Service Centre — CAD Library
 * Model filtering plus a lazily-loaded <model-viewer> 3D preview.
 * No external dependencies at load time — the 955 KB model-viewer module is
 * injected on the FIRST "View in 3D" click and never before.
 *
 * Expected DOM structure (see sections/cad-library.liquid):
 *   .cad-library[data-section-id][data-viewer-src]   — root
 *     select.js-cad-model-filter                     — model dropdown
 *     .cad-section[data-source]                      — Official / Community wrappers
 *       .cad-card[data-model][data-source][data-glb][data-title]
 *         button.cad-card__view                      — opens the viewer
 *         a.cad-card__download                       — STEP download
 *     .cad-library__empty                            — no-results message
 *     .cad-lightbox                                  — viewer shell
 *       .cad-lightbox__stage                         — <model-viewer> goes here
 *       .cad-lightbox__close
 */

document.addEventListener('DOMContentLoaded', function () {

  var roots = document.querySelectorAll('.cad-library');
  if (!roots.length) return;

  /* cascadio GLBs carry NO material — without this the part renders white-on-white. */
  var BASE_COLOR = [0.30, 0.42, 0.60, 1];

  /* ---- Lazy <model-viewer> module loader (injected once per page) ---- */
  var viewerPromise = null;

  function loadViewer(src) {
    if (viewerPromise) return viewerPromise;
    viewerPromise = new Promise(function (resolve, reject) {
      var el = document.createElement('script');
      el.type = 'module';
      el.src = src;
      el.onload = function () {
        if (!window.customElements) { reject(new Error('no custom elements')); return; }
        window.customElements.whenDefined('model-viewer').then(resolve, reject);
      };
      el.onerror = function () { reject(new Error('model-viewer failed to load')); };
      document.head.appendChild(el);
    });
    return viewerPromise;
  }

  roots.forEach(function (root) {

    var modelFilter = root.querySelector('.js-cad-model-filter');
    var sections    = root.querySelectorAll('.cad-section');
    var cards       = root.querySelectorAll('.cad-card');
    var emptyMsg    = root.querySelector('.cad-library__empty');
    var lightbox    = root.querySelector('.cad-lightbox');
    var stage       = lightbox ? lightbox.querySelector('.cad-lightbox__stage') : null;
    var lbTitle     = lightbox ? lightbox.querySelector('.cad-lightbox__title') : null;
    var closeBtn    = lightbox ? lightbox.querySelector('.cad-lightbox__close') : null;
    var viewerSrc   = root.getAttribute('data-viewer-src') || '';
    var lastFocused = null;

    /* ---- Filtering ---- */

    function applyFilter() {
      var activeModel = (modelFilter && modelFilter.value) || 'all';
      var visibleTotal = 0;

      cards.forEach(function (card) {
        var show = activeModel === 'all' || card.getAttribute('data-model') === activeModel;
        card.style.display = show ? '' : 'none';
        if (show) visibleTotal++;
      });

      /* Hide any Official/Community wrapper with zero visible cards. */
      sections.forEach(function (sec) {
        var visible = 0;
        sec.querySelectorAll('.cad-card').forEach(function (card) {
          if (card.style.display !== 'none') visible++;
        });
        sec.style.display = visible === 0 ? 'none' : '';
      });

      if (emptyMsg) emptyMsg.style.display = visibleTotal === 0 ? '' : 'none';
    }

    if (modelFilter) modelFilter.addEventListener('change', applyFilter);

    /* ---- Lightbox ---- */

    function closeLightbox() {
      if (!lightbox) return;
      lightbox.setAttribute('aria-hidden', 'true');
      if (stage) stage.innerHTML = '';   /* destroy <model-viewer> to free GPU memory */
      document.body.style.overflow = '';
      document.removeEventListener('keydown', escHandler);
      if (lastFocused) { lastFocused.focus(); lastFocused = null; }
    }

    function escHandler(e) { if (e.key === 'Escape') closeLightbox(); }

    function openLightbox(card) {
      if (!lightbox || !stage) return;
      var glb = card.getAttribute('data-glb');
      var title = card.getAttribute('data-title') || '';
      if (!glb) return;

      if (lbTitle) lbTitle.textContent = title;
      stage.innerHTML = '';

      var viewer = document.createElement('model-viewer');
      viewer.setAttribute('src', glb);
      viewer.setAttribute('alt', title);
      viewer.setAttribute('camera-controls', '');
      viewer.setAttribute('auto-rotate', '');
      viewer.setAttribute('shadow-intensity', '1');
      viewer.setAttribute('exposure', '0.95');
      viewer.setAttribute('environment-image', 'neutral');
      viewer.addEventListener('load', function () {
        var model = viewer.model;
        if (!model || !model.materials) return;
        model.materials.forEach(function (m) {
          if (m.pbrMetallicRoughness) m.pbrMetallicRoughness.setBaseColorFactor(BASE_COLOR);
        });
      });
      stage.appendChild(viewer);

      lightbox.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
      document.addEventListener('keydown', escHandler);
      if (closeBtn) closeBtn.focus();
    }

    /* A broken viewer must never block the download. */
    function showViewerFallback(card) {
      if (!lightbox || !stage) return;
      var link = card.querySelector('.cad-card__download');
      if (lbTitle) lbTitle.textContent = card.getAttribute('data-title') || '';

      var wrap = document.createElement('div');
      wrap.className = 'cad-lightbox__fallback';
      var msg = document.createElement('p');
      msg.textContent = 'The 3D preview could not be loaded.';
      wrap.appendChild(msg);
      if (link) {
        var p = document.createElement('p');
        var a = document.createElement('a');
        a.href = link.href;                /* resolved by the browser, never string-built */
        a.setAttribute('download', '');
        a.textContent = 'Download the CAD file instead';
        p.appendChild(a);
        wrap.appendChild(p);
      }
      stage.innerHTML = '';
      stage.appendChild(wrap);
      lightbox.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
      document.addEventListener('keydown', escHandler);
      if (closeBtn) closeBtn.focus();
    }

    root.querySelectorAll('.cad-card__view').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var card = btn.closest('.cad-card');
        if (!card) return;
        lastFocused = btn;
        btn.disabled = true;
        loadViewer(viewerSrc)
          .then(function () { openLightbox(card); })
          .catch(function () { showViewerFallback(card); })
          .then(function () { btn.disabled = false; });
      });
    });

    if (closeBtn) closeBtn.addEventListener('click', closeLightbox);
    if (lightbox) {
      var overlay = lightbox.querySelector('.cad-lightbox__overlay');
      if (overlay) overlay.addEventListener('click', closeLightbox);
    }

    applyFilter();
  });

}); /* end DOMContentLoaded */
